/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  <skio-plan-picker 
    product='{{ product | json | escape }}'
    selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
    formId='{{ product_form_id }}'
    >
  </skio-plan-picker>
  <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>

  Note: formId not required if element is inside of a form already
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';
import { repeat } from 'https://cdn.jsdelivr.net/npm/lit-html@2.8.0/directives/repeat.js';

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    border: 0;
    margin: 0;
  }
  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    box-shadow: none;
    border-radius: 8px;
    border-width: 1px;
    border-color: #ebebeb;
    border-style: solid;
    transition: border-color 0.2s ease;
    width: 100%;
    background: #ffffff;
  }
  .skio-group-container--selected {
    border-color: #7cbcc6;
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }

  .skio-group-label {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 16px;
    overflow: hidden;
  }

  .skio-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0;
    color: #000000;
  }

  .skio-radio__container {
    display: flex;
    margin-right: 10px;
    color: #7cbcc6;
  }

  .skio-radio {
    transition:
      transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  .skio-price {
    margin-left: auto;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0;
    color: #000000;
  }

  .skio-group-content {
    width: auto;
    margin-left: 30px;
    margin-top: 4px;
  }

  .skio-subtitle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .skio-subtitle {
    margin: 0;
    color: #6b7280;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 0;
  }

  .skio-info-trigger {
    border: none;
    background: transparent;
    color: #7cbcc6;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }

  .skio-info-trigger svg {
    width: 18px;
    height: 18px;
  }

  .skio-group-title {
    min-width: max-content;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .skio-save {
    padding: 4px 8px;
    border-radius: 2px;
    background: #7cbcc6;

    font-weight: 500;
    font-size: 8px;
    line-height: 12px;
    letter-spacing: 0;
    color: #ffffff;
  }

  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 10px 36px 10px 12px;
    margin-top: 8px;
    border-radius: 5px;
    background-color: #f3f3f3;
    color: #222;
    width: 100%;
    border: 1px solid #ececec;
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }

  .skio-frequency option {
    color: black;
  }

  .skio-group-container:not(.skio-group-container--selected) .skio-frequency {
    display: none;
  }

  .skio-benefit-teaser {
    margin-top: 10px;
    border-radius: 8px;
    background: #dff3f6;
    padding: 12px 14px;
    display: none;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .skio-benefit-teaser__title {
    margin: 0 0 8px;
    color: ##000000;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0;
  }

  .skio-benefit-teaser__text {
    margin: 0;
    color: #6b7280;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0;
  }

  .skio-benefit-teaser__open {
    border: none;
    background: transparent;
    color: #7cbcc6;
    font-size: 28px;
    line-height: 1;
    cursor: pointer;
    padding: 0;

    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0;
  }

  .skio-benefit-teaser__open span {
    margin-left: 5px;
  }

  .skio-group-container--show-info .skio-benefit-teaser {
    display: flex;
  }
  .skio-benefit-teaser__content {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  @media screen and (max-width: 767px) {
    .skio-plan-picker {
      flex-direction: column;
    }
    .skio-group-container--available {
      width: 100%;
    }
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object }, //required
    productHandle: { type: String }, //optional (unless product isn't passed, then required)
    key: { type: String }, //optional, defaults to product.id; identifier for this instance of the Skio plan picker

    formId: { type: String }, //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean }, //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String }, //optional, defaults to percent; can also pass "fixed"

    currency: { type: String }, //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String }, //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {}, //placeholder for object

    externalPriceSelector: { type: String }, //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String }, //optional, used to update the external price

    selectedVariant: { type: Object }, //placeholder for data
    skioSellingPlanGroups: {}, //placeholder for data
    availableSellingPlanGroups: {}, //placeholder for data
    selectedSellingPlanGroup: {}, //placeholder for data
    selectedSellingPlan: {}, //placeholder for data

    defaultFrequency: {}, //placeholder for data
    activeInfoGroupId: { type: String },
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = 'onetime';

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.externalPriceSelector = '[skio-external-price]';

    this.externalPriceSelectorWithCurrency = '[skio-external-price-with-currency]';

    this.currency = 'USD';
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;
    this.activeInfoGroupId = null;

    this.variantChangeUnsubscriber = null;
    this._pendingVariantId = null;
    this._lastSyncedVariantId = null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    if (!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }

    this.bindVariantChangeListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variantChanged', this._onDocumentVariantChanged);
    this.variantChangeUnsubscriber?.();
    this.variantChangeUnsubscriber = null;
  }

  bindVariantChangeListeners() {
    document.addEventListener('variantChanged', this._onDocumentVariantChanged);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (variant?.id) this.handleVariantChange(variant);
      });
    }
  }

  _onDocumentVariantChanged = (e) => {
    const variantId = e.detail?.variantId ?? e.detail?.variant?.id;
    if (variantId) this.handleVariantChange(variantId);
  };

  handleVariantChange(variantOrId) {
    if (!this.product?.variants?.length) return;

    let variant =
      variantOrId && typeof variantOrId === 'object' && variantOrId.id != null
        ? this.product.variants.find((v) => v.id == variantOrId.id) || variantOrId
        : null;

    if (!variant) {
      const variantId = variantOrId;
      variant = this.product.variants.find((v) => v.id == variantId);
    }

    if (!variant) {
      this.log('Unable to find variant with id: ', variantOrId);
      return;
    }

    if (this.selectedVariant?.id === variant.id) return;

    // Defer so we don't update during product-info / lit-html's own render pass
    this._pendingVariantId = variant.id;
    queueMicrotask(() => {
      if (this._pendingVariantId !== variant.id) return;
      this._pendingVariantId = null;
      if (this.selectedVariant?.id === variant.id) return;
      this.selectedVariant = variant;
    });
  }

  initSkioSellingPlanGroups() {
    if (!this.product?.selling_plan_groups) {
      this.skioSellingPlanGroups = [];
      return;
    }

    this.key = this.key ? this.key : this.product.id;

    this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
      (selling_plan_group) => selling_plan_group.app_id === 'SKIO',
    );

    this.skioSellingPlanGroups.forEach((group) => {
      group.selling_plans.sort(function (a, b) {
        if (parseInt(a.name.replace(/\D/g, '')) < parseInt(b.name.replace(/\D/g, ''))) return -1;
        if (parseInt(a.name.replace(/\D/g, '')) > parseInt(b.name.replace(/\D/g, ''))) return 1;
        if (parseInt(a.name.replace(/\D/g, '')) == parseInt(b.name.replace(/\D/g, ''))) return 0;
      });
    });
  }

  getVariantWithAllocations() {
    if (!this.selectedVariant) return null;
    return this.product?.variants?.find((v) => v.id == this.selectedVariant.id) || this.selectedVariant;
  }

  syncSellingPlansForVariant() {
    const variant = this.getVariantWithAllocations();

    if (!variant || !this.skioSellingPlanGroups?.length) {
      this.availableSellingPlanGroups = [];
      return;
    }

    this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter((selling_plan_group) =>
      selling_plan_group.selling_plans.some((selling_plan) =>
        variant.selling_plan_allocations?.some(
          (allocation) => allocation.selling_plan_id === selling_plan.id,
        ),
      ),
    );

    if (!this.availableSellingPlanGroups.length) {
      this.selectedSellingPlan = null;
      this.selectedSellingPlanGroup = null;
      return;
    }

    this.availableSellingPlanGroups.forEach((group) => {
      const previousPlanId =
        group.selected_selling_plan?.id ??
        (this.selectedSellingPlanGroup?.id === group.id ? this.selectedSellingPlan?.id : null);

      let selling_plan = previousPlanId
        ? group.selling_plans.find((p) => p.id == previousPlanId)
        : null;

      if (!selling_plan && this.defaultFrequency) {
        selling_plan = group.selling_plans.find((x) =>
          x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()),
        );
      }

      group.selected_selling_plan = selling_plan || group.selling_plans[0];
    });

    const wantsSubscription =
      this.startSubscription === true ||
      this.product.requires_selling_plan === true ||
      this.purchaseOption === 'subscription';

    if (!wantsSubscription) {
      this.selectedSellingPlan = null;
      this.selectedSellingPlanGroup = null;
      return;
    }

    const sellingPlanName = this.selectedSellingPlan?.name;
    const sellingPlanGroup = sellingPlanName
      ? this.availableSellingPlanGroups.find((x) => x.selling_plans.some((y) => y.name === sellingPlanName))
      : null;

    if (sellingPlanGroup) {
      const sellingPlan = sellingPlanGroup.selling_plans.find((y) => y.name === sellingPlanName);
      this.selectedSellingPlanGroup = sellingPlanGroup;
      this.selectedSellingPlan = sellingPlan || sellingPlanGroup.selected_selling_plan;
    } else {
      this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
      this.selectedSellingPlan = this.availableSellingPlanGroups[0].selected_selling_plan;
    }
  }

  willUpdate(changedProperties) {
    if (!this.product || !this.selectedVariant) return;

    this.initSkioSellingPlanGroups();

    const variantChanged = changedProperties.has('selectedVariant');
    const needsInitialSync = this._lastSyncedVariantId !== this.selectedVariant.id;

    if (variantChanged || needsInitialSync) {
      this.syncSellingPlansForVariant();
      this._lastSyncedVariantId = this.selectedVariant.id;
    }
  }

  toggleInfoBox = (e, groupId) => {
    e.preventDefault();
    e.stopPropagation();
    const key = String(groupId);
    this.activeInfoGroupId = this.activeInfoGroupId === key ? null : key;
  };

  openDetailsModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.dispatchEvent(
      new CustomEvent('skio:open-details-modal', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  render() {
    if (!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0) return;

    return html` <fieldset class="skio-plan-picker" skio-plan-picker="${this.key}">
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ''}
        name="selling_plan"
        type="hidden"
        value="${this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}"
      />
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ''}
        name="properties[Discount]"
        type="hidden"
        value="${this.selectedSellingPlan !== null ? this.discount(this.selectedSellingPlan).percent : ''}"
        ?disabled="${this.selectedSellingPlan == null ? true : false}"
      />

      <div
        class="skio-group-container ${this.product.requires_selling_plan == false
          ? 'skio-group-container--available'
          : ''} ${this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : ''} ${this
          .subscriptionFirst
          ? 'skio-onetime-second'
          : ''}"
        skio-group-container
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="skio-one-time-${this.key}"
          class="skio-group-input"
          name="skio-group-${this.key}"
          type="radio"
          value=""
          skio-one-time
          ?checked=${this.startSubscription == false && this.product.requires_selling_plan == false ? true : false}
        />
        <label class="skio-group-label" for="skio-one-time-${this.key}">
          <div class="skio-group-topline">
            <div class="skio-radio__container">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#7CBCC6" stroke-width="2"></circle>
                <circle class="skio-radio" cx="12" cy="12" r="7" fill="#7CBCC6"></circle>
              </svg>
            </div>
            One-time Purchase
            <div class="skio-price">
              <span skio-onetime-price>${this.moneyFormatter.format(this.selectedVariant.price / 100)}</span>
            </div>
          </div>
        </label>
      </div>
      ${this.availableSellingPlanGroups?.length
        ? repeat(
            this.availableSellingPlanGroups,
            (group) => group.id,
            (group, index) => html`
              <div
                class="skio-group-container skio-group-container--available ${this.selectedSellingPlanGroup == group
                  ? 'skio-group-container--selected'
                  : ''} ${this.activeInfoGroupId === String(group.id) ? 'skio-group-container--show-info' : ''}"
                skio-group-container
                @click=${() => this.selectSellingPlanGroup(group)}
              >
                <input
                  id="skio-selling-plan-group-${index}-${this.key}"
                  class="skio-group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="${group.id}"
                  skio-selling-plan-group="${group.id}"
                  ?checked=${this.selectedSellingPlanGroup == group ? true : false}
                />
                <label class="skio-group-label" for="skio-selling-plan-group-${index}-${this.key}">
                  <div class="skio-group-topline">
                    <div class="skio-radio__container">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                        <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
                      </svg>
                    </div>
                    <div class="skio-group-title">
                      ${group.name == 'Subscription' ? 'Subscribe & Save' : group.name}
                      ${this.discount(group.selected_selling_plan).percent !== '0%'
                        ? html`
                            <span class="skio-save"
                              >Save
                              <span skio-discount
                                >${this.discountFormat == 'percent'
                                  ? this.discount(group.selected_selling_plan).percent
                                  : this.discount(group.selected_selling_plan).amount}</span
                              ></span
                            >
                          `
                        : html``}
                    </div>
                    <div class="skio-price">
                      <span skio-subscription-price>${this.price(group.selected_selling_plan)}</span>
                    </div>
                  </div>
                  <div class="skio-group-content">
                    <div class="skio-subtitle-row">
                      <p class="skio-subtitle">${this.deliveredScheduleSubtitle(group)}</p>
                      <button class="skio-info-trigger" @click=${(e) => this.toggleInfoBox(e, group.id)}>
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"></circle>
                          <path d="M12 10.3v5.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
                          <circle cx="12" cy="7.5" r="1.1" fill="currentColor"></circle>
                        </svg>
                      </button>
                    </div>
                    <select
                      skio-selling-plans="${group.id}"
                      class="skio-frequency"
                      .value=${String(group.selected_selling_plan?.id ?? '')}
                      @change=${(e) => this.selectSellingPlan(e.target, group)}
                    >
                      ${group
                        ? group.selling_plans.map(
                            (selling_plan) => html`
                              <option value="${selling_plan.id}">
                                ${group.name == 'Subscription'
                                  ? `Delivery ${selling_plan.name.toLowerCase()}`
                                  : `${selling_plan.name}`}
                              </option>
                            `,
                          )
                        : ''}
                    </select>
                  </div>
                  <div class="skio-benefit-teaser" @click=${this.openDetailsModal}>
                    <div class="skio-benefit-teaser__content">
                      <img
                        src="https://cdn.shopify.com/s/files/1/1570/4325/files/image_95.png?v=1777986054"
                        alt="Save Benefits Icon"
                        width="24"
                        height="24"
                        loading="lazy"
                        class="skio-icon-image"
                      />
                      <div>
                        <p class="skio-benefit-teaser__title">Save up to 5% on every shipment</p>
                        <p class="skio-benefit-teaser__text">Plus more benefits with Subscribe & Save!</p>
                      </div>
                    </div>
                    <button class="skio-benefit-teaser__open" @click=${this.openDetailsModal} aria-label="Open details">
                      See All
                      <span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="4" height="7" viewBox="0 0 4 7" fill="none">
                          <path
                            d="M0.146447 6.14645C-0.0488156 6.34171 -0.0488156 6.65829 0.146447 6.85355C0.341709 7.04882 0.658291 7.04882 0.853553 6.85355L3.85355 3.85355C4.04882 3.65829 4.04882 3.34171 3.85355 3.14645L0.853553 0.146448C0.658291 -0.0488148 0.341708 -0.0488148 0.146446 0.146448C-0.0488158 0.34171 -0.0488158 0.658292 0.146446 0.853554L2.79289 3.5L0.146447 6.14645Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </label>
              </div>
            `,
          )
        : ''}
    </fieldset>`;
  }

  updated = (changed) => {
    if (changed.has('product') && this.product) {
      this.initSkioSellingPlanGroups();
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      this.updateForm();
      this.updateExternalPrice();
    }

    if (changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${this.key}"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? (el.style.display = 'none') : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${this.key}"]`).forEach((el) => {
        this.selectedSellingPlan == null ? (el.style.display = 'none') : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();
      this.updateExternalPrice();
    }

    if (changed.has('formId')) {
      //update the form that was passed, if any
      this.updateForm();
    }
  };

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  };

  error = (...args) => {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  };
  //this is where the issue of price is happening, we have to remove  + ' ' + this.currency
  updateExternalPrice() {
    document.querySelectorAll(this.externalPriceSelector).forEach((el) => {
      el.innerHTML = this.price(this.selectedSellingPlan);
    });
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      el.innerHTML = this.price(this.selectedSellingPlan);
    });
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (group) this.purchaseOption = 'subscription';
    else this.purchaseOption = 'onetime';

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.purchaseOption = 'subscription';
      this.updateForm();
      this.updateExternalPrice();
      this.requestUpdate();
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          ' for variant ' +
          this.selectedVariant.id +
          ' from product ' +
          this.product.id +
          ' : ' +
          this.product.handle,
      );
  }

  /**
   * Subtitle under Subscribe & Save: "Delivered …" matches the selected selling plan (same interval as the dropdown).
   */
  deliveredScheduleSubtitle(group) {
    const tail = ' Skip, pause, or cancel anytime.';
    const plan = group?.selected_selling_plan;
    if (!plan?.name) return 'Delivered every 30 days.' + tail;
    const lower = plan.name.toLowerCase();
    if (group.name === 'Subscription') {
      const lead = lower.startsWith('every ') ? `Delivered ${lower}.` : `Delivered every ${lower}.`;
      return lead + tail;
    }
    return `${plan.name}.` + tail;
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: '0%', amount: 0 };

    const price_adjustment = selling_plan.price_adjustments[0];
    const discount = { percent: '0%', amount: 0 };
    const price = this.selectedVariant.price;

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`;
        discount.amount = Math.round((price * price_adjustment.value) / 100.0);
        break;
      case 'fixed_amount':
        discount.percent = `${Math.round(((price_adjustment.value * 1.0) / price) * 100.0)}%`;
        discount.amount = price_adjustment.value;
        break;
      case 'price':
        discount.percent = `${Math.round((((price - price_adjustment.value) * 1.0) / price) * 100.0)}%`;
        discount.amount = price - price_adjustment.value;
        break;
    }

    return discount;
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    return formatted
      ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount)
      : this.selectedVariant.price - this.discount(selling_plan).amount;
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.querySelector(`#${this.formId}`);

      if (form) {
        let selling_plan_input = form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement('input');
          selling_plan_input.type = 'hidden';
          selling_plan_input.name = 'selling_plan';
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }

        let discount_input = form.querySelector('[name="properties[Discount]"]');
        if (discount_input) {
          discount_input.value =
            this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          discount_input = document.createElement('input');
          discount_input.type = 'hidden';
          discount_input.name = 'properties[Discount]';
          discount_input.value =
            this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(discount_input);
        }
      } else {
        console.log(`Skio error: form ID is ${this.formId}, but no form with that ID was found.`);
      }
    }
  }

  // Optional functions keep if necessary

  /**
   *
   *
   */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id }),
      },
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        this.log('SKIO added item to cart: ', response);
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        });

        this.dispatchEvent(event);
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error);
      });
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product;
        this.selectedVariant = product.variants[0];

        return product;
      });
  };
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);
